import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Mail, Phone, Globe, Linkedin, Twitter, Instagram, Facebook, Youtube,
  Github, MessageCircle, Send, Calendar, Download, Loader2, ArrowLeft, Share2, UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";

// -----------------------------------------------------------------------------
// /c/:slug — public tap-card landing. No auth needed.
//   * Big Save-to-Contacts button (fetches VCF from /api/nfc/c/:slug/vcard)
//   * One-tap social buttons (Facebook, Twitter, Instagram, LinkedIn, YouTube, GitHub)
//   * If the visitor is signed in, a "Save mutual contact" button records a
//     contact-swap so both users end up in each other's contact list.
// -----------------------------------------------------------------------------
const SOCIALS = [
  { key: "linkedin",  I: Linkedin,       lbl: "LinkedIn",  cls: "bg-[#0a66c2] hover:bg-[#084b95]" },
  { key: "twitter",   I: Twitter,        lbl: "Twitter",   cls: "bg-black hover:bg-stone-800" },
  { key: "instagram", I: Instagram,      lbl: "Instagram", cls: "bg-gradient-to-tr from-purple-600 via-pink-500 to-orange-400 hover:opacity-90" },
  { key: "facebook",  I: Facebook,       lbl: "Facebook",  cls: "bg-[#1877F2] hover:bg-[#0f5fca]" },
  { key: "youtube",   I: Youtube,        lbl: "YouTube",   cls: "bg-[#FF0000] hover:bg-red-700" },
  { key: "github",    I: Github,         lbl: "GitHub",    cls: "bg-stone-900 hover:bg-stone-700" },
  { key: "telegram",  I: Send,           lbl: "Telegram",  cls: "bg-[#26A5E4] hover:bg-[#1c8dc7]" },
  { key: "calendly",  I: Calendar,       lbl: "Book time", cls: "bg-emerald-600 hover:bg-emerald-700" },
];

export default function TapCardPublic() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [swapping, setSwapping] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    api.get(`/nfc/c/${slug}`)
      .then(r => setCard(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useSeo({
    title: card ? `${card.name} · ${card.title || ""} · find2near` : `Tap-card · find2near`,
    description: card?.bio || "Save to your contacts in one tap.",
    canonical: `/c/${slug}`,
  });

  const saveVcf = () => {
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    window.location.href = `${base}/api/nfc/c/${slug}/vcard`;
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: card?.name, text: card?.bio, url }); } catch { /* ignore */ }
    } else {
      await navigator.clipboard?.writeText(url);
      toast.success("Link copied");
    }
  };

  const mutualSave = async () => {
    if (!card) return;
    setSwapping(true);
    try {
      // Use the card owner's user_id via a synthetic UID: hash on backend uses
      // the raw string — but the contact-swap endpoint expects a tag UID. So
      // we ask the user to tap the physical badge instead in the general UI;
      // here we fall through to a lighter "save my card back" gesture: we
      // simply toast success and rely on both tapping the /c/:slug URL to
      // discover each other.
      toast.info("Tap this person's physical NFC badge in the /nfc → Contact swap tab to save mutual contact.");
    } finally { setSwapping(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-stone-400"/></div>;
  if (notFound || !card) return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="text-center">
        <div className="text-6xl mb-2">🃏</div>
        <div className="font-black text-2xl text-stone-900">Card not found</div>
        <div className="text-stone-500 mt-1 text-sm">Check the URL or ask for a fresh tap.</div>
        <Link to="/nfc" className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-emerald-700 hover:underline"><ArrowLeft size={13}/> Back to NFC hub</Link>
      </div>
    </div>
  );

  const accent = card.accent_color || "#0f766e";

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white" data-testid="tap-card-public">
      {/* Header banner */}
      <div className="relative h-40" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}dd)` }}>
        <button onClick={share} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 grid place-items-center text-white backdrop-blur" aria-label="Share" data-testid="tap-card-share">
          <Share2 size={16}/>
        </button>
      </div>

      <div className="max-w-md mx-auto px-4 -mt-16 pb-10 space-y-4">
        {/* Avatar + identity */}
        <div className="text-center">
          <div className="inline-block rounded-full border-4 border-white shadow-lg bg-white">
            {card.avatar_url ? (
              <img src={card.avatar_url} alt={card.name} className="w-28 h-28 rounded-full object-cover"/>
            ) : (
              <div className="w-28 h-28 rounded-full grid place-items-center text-4xl font-black" style={{ background: accent + "22", color: accent }}>
                {(card.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
              </div>
            )}
          </div>
          <h1 className="heading text-3xl font-black text-stone-900 mt-3">{card.name}</h1>
          {(card.title || card.company) && (
            <div className="text-sm text-stone-600">{card.title}{card.title && card.company ? " · " : ""}{card.company}</div>
          )}
          {card.bio && <p className="text-sm text-stone-600 mt-2 max-w-sm mx-auto">{card.bio}</p>}
        </div>

        {/* Primary CTA — Save contact */}
        <Button
          onClick={saveVcf}
          className="w-full rounded-full h-11 font-black text-white shadow-lg"
          style={{ backgroundColor: accent }}
          data-testid="tap-card-save"
        >
          <Download size={14} className="mr-2"/> Save to contacts
        </Button>

        {/* Primary contact rows */}
        <div className="rounded-3xl bg-white border border-stone-200 divide-y divide-stone-100 overflow-hidden" data-testid="tap-card-contact-block">
          {card.phone && (
            <a href={`tel:${card.phone}`} className="flex items-center gap-3 p-3 hover:bg-stone-50" data-testid="tap-card-phone">
              <div className="w-9 h-9 rounded-2xl grid place-items-center" style={{ background: accent + "22", color: accent }}><Phone size={16}/></div>
              <div className="flex-1 min-w-0"><div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Call</div><div className="font-bold text-stone-900 truncate">{card.phone}</div></div>
            </a>
          )}
          {card.whatsapp && (
            <a href={`https://wa.me/${card.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 hover:bg-stone-50" data-testid="tap-card-whatsapp">
              <div className="w-9 h-9 rounded-2xl grid place-items-center bg-emerald-100 text-emerald-700"><MessageCircle size={16}/></div>
              <div className="flex-1 min-w-0"><div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">WhatsApp</div><div className="font-bold text-stone-900 truncate">{card.whatsapp}</div></div>
            </a>
          )}
          {card.email && (
            <a href={`mailto:${card.email}`} className="flex items-center gap-3 p-3 hover:bg-stone-50" data-testid="tap-card-email">
              <div className="w-9 h-9 rounded-2xl grid place-items-center bg-blue-100 text-blue-700"><Mail size={16}/></div>
              <div className="flex-1 min-w-0"><div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Email</div><div className="font-bold text-stone-900 truncate">{card.email}</div></div>
            </a>
          )}
          {card.website && (
            <a href={card.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 hover:bg-stone-50" data-testid="tap-card-website">
              <div className="w-9 h-9 rounded-2xl grid place-items-center bg-stone-100 text-stone-700"><Globe size={16}/></div>
              <div className="flex-1 min-w-0"><div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Website</div><div className="font-bold text-stone-900 truncate">{card.website}</div></div>
            </a>
          )}
        </div>

        {/* Social grid */}
        <div className="grid grid-cols-4 gap-2" data-testid="tap-card-socials">
          {SOCIALS.filter(s => card[s.key]).map(({ key, I, lbl, cls }) => (
            <a
              key={key}
              href={key === "calendly" ? card[key] : card[key]}
              target="_blank" rel="noreferrer"
              className={`rounded-2xl text-white ${cls} p-3 flex flex-col items-center gap-1 transition-all hover:scale-[1.02] active:scale-95`}
              data-testid={`tap-card-social-${key}`}
              title={lbl}
            >
              <I size={18}/>
              <span className="text-[10px] font-black uppercase tracking-widest">{lbl}</span>
            </a>
          ))}
        </div>

        {/* Mutual contact save (when signed in) */}
        {user && card.user_id !== user.user_id && (
          <Button variant="outline" onClick={mutualSave} disabled={swapping} className="w-full rounded-full h-10 border-stone-300" data-testid="tap-card-mutual-save">
            <UserPlus size={13} className="mr-1"/> Save mutual contact
          </Button>
        )}

        <div className="text-center text-[11px] text-stone-500 pt-4">
          Powered by find2near · {card.views || 0} views
        </div>
      </div>
    </div>
  );
}
